import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Gemeinde from "../DTOs/Gemeinde";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import { HOLIDAY_REGIONS } from "../utils/germanHolidays";
import Spinner from "react-bootstrap/esm/Spinner";

/** Same list as functions/src/gemeindeProfile.ts. */

type Profile = Pick<
    Gemeinde,
    "senderName" | "senderSignature" | "contactEmail" | "contactPhone" | "address" | "logoUrl" | "brandColor" | "defaultLanguage" | "holidayRegion"
> & { recordingLocationCode: string; recordingLocationLabel: string };

interface Props {
    gemeinde: Gemeinde;
    onSaved: () => void;
    notify: (variant: string, message: string) => void;
}

/**
 * The Gemeinde's own master data (GOD-45): what mails are signed with, how to
 * reach the Gemeinde, logo and colour, language and holiday region. Saved
 * through updateGemeinde, which checks every field again.
 */
export default function GemeindeProfileForm({ gemeinde, onSaved, notify }: Props) {
    const { t } = useTranslation();
    const fromGemeinde = (g: Gemeinde): Profile => ({
        senderName: g.senderName,
        senderSignature: g.senderSignature,
        contactEmail: g.contactEmail,
        contactPhone: g.contactPhone,
        address: g.address,
        logoUrl: g.logoUrl,
        brandColor: g.brandColor,
        defaultLanguage: g.defaultLanguage,
        holidayRegion: g.holidayRegion,
        // Never set: the legacy location still applies (GOD-46), shown so it can be confirmed.
        recordingLocationCode: g.recordingLocationCode ?? "SE",
        recordingLocationLabel: g.recordingLocationCode === undefined ? "Sennestadt" : g.recordingLocationLabel ?? "",
    });
    const [profile, setProfile] = useState<Profile>(fromGemeinde(gemeinde));
    const [saving, setSaving] = useState(false);
    useEffect(() => setProfile(fromGemeinde(gemeinde)), [gemeinde]);

    const set = (patch: Partial<Profile>) => setProfile((p) => ({ ...p, ...patch }));

    async function handleSave(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true);
        try {
            await httpsCallable(functions, "updateGemeinde")({ gemeindeId: gemeinde.id, ...profile });
            notify("success", t("gemeindeProfile.saved"));
            onSaved();
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setSaving(false);
    }

    return (
        <Form onSubmit={handleSave} data-testid="gemeinde-profile-form">
            <Row className="g-3">
                <Col md={6}>
                    <Form.Group controlId="profile.senderName">
                        <Form.Label>{t("gemeindeProfile.senderName")}</Form.Label>
                        <Form.Control value={profile.senderName} maxLength={80} onChange={(e) => set({ senderName: e.target.value })} />
                        <Form.Text className="text-muted">{t("gemeindeProfile.senderNameHelp")}</Form.Text>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="profile.contactEmail">
                        <Form.Label>{t("gemeindeProfile.contactEmail")}</Form.Label>
                        <Form.Control type="email" value={profile.contactEmail} maxLength={254} onChange={(e) => set({ contactEmail: e.target.value })} />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group controlId="profile.senderSignature">
                        <Form.Label>{t("gemeindeProfile.senderSignature")}</Form.Label>
                        <Form.Control as="textarea" rows={3} maxLength={500} value={profile.senderSignature} onChange={(e) => set({ senderSignature: e.target.value })} />
                        <Form.Text className="text-muted">{t("gemeindeProfile.senderSignatureHelp")}</Form.Text>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="profile.contactPhone">
                        <Form.Label>{t("gemeindeProfile.contactPhone")}</Form.Label>
                        <Form.Control type="tel" value={profile.contactPhone} maxLength={40} onChange={(e) => set({ contactPhone: e.target.value })} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="profile.address">
                        <Form.Label>{t("gemeindeProfile.address")}</Form.Label>
                        <Form.Control value={profile.address} maxLength={300} onChange={(e) => set({ address: e.target.value })} />
                    </Form.Group>
                </Col>
                <Col md={8}>
                    <Form.Group controlId="profile.logoUrl">
                        <Form.Label>{t("gemeindeProfile.logoUrl")}</Form.Label>
                        <Form.Control type="url" placeholder="https://…" value={profile.logoUrl} maxLength={500} onChange={(e) => set({ logoUrl: e.target.value })} />
                        <Form.Text className="text-muted">{t("gemeindeProfile.logoUrlHelp")}</Form.Text>
                    </Form.Group>
                    {profile.logoUrl.startsWith("https://") && (
                        <img src={profile.logoUrl} alt={t("gemeindeProfile.logoPreview") as string} className="mt-2" style={{ maxHeight: 56, maxWidth: 200 }} />
                    )}
                </Col>
                <Col md={4}>
                    <Form.Group controlId="profile.brandColor">
                        <Form.Label>{t("gemeindeProfile.brandColor")}</Form.Label>
                        <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                                type="color"
                                value={profile.brandColor || "#c8a24b"}
                                onChange={(e) => set({ brandColor: e.target.value })}
                                style={{ width: 56 }}
                            />
                            <code>{profile.brandColor || "—"}</code>
                            {profile.brandColor && (
                                <Button size="sm" variant="link" onClick={() => set({ brandColor: "" })}>
                                    {t("gemeindeProfile.reset")}
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="profile.defaultLanguage">
                        <Form.Label>{t("superadmin.language")}</Form.Label>
                        <Form.Select value={profile.defaultLanguage} onChange={(e) => set({ defaultLanguage: e.target.value as Profile["defaultLanguage"] })}>
                            <option value="de">Deutsch</option>
                            <option value="en">English</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="profile.holidayRegion">
                        <Form.Label>{t("gemeindeProfile.holidayRegion")}</Form.Label>
                        <Form.Select value={profile.holidayRegion} onChange={(e) => set({ holidayRegion: e.target.value })}>
                            <option value="">—</option>
                            {HOLIDAY_REGIONS.map((code) => (
                                <option key={code} value={code}>
                                    {t(`gemeindeProfile.regions.${code}`)} ({code})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={8}>
                    <Form.Group controlId="profile.recordingLocationLabel">
                        <Form.Label>{t("gemeindeProfile.recordingLocationLabel")}</Form.Label>
                        <Form.Control value={profile.recordingLocationLabel} maxLength={60} onChange={(e) => set({ recordingLocationLabel: e.target.value })} />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group controlId="profile.recordingLocationCode">
                        <Form.Label>{t("gemeindeProfile.recordingLocationCode")}</Form.Label>
                        <Form.Control
                            value={profile.recordingLocationCode}
                            maxLength={4}
                            onChange={(e) => set({ recordingLocationCode: e.target.value.toUpperCase() })}
                        />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Text className="text-muted">{t("gemeindeProfile.recordingLocationHelp")}</Form.Text>
                </Col>
            </Row>
            <Button type="submit" className="mt-3" disabled={saving}>
                {t("admin.save")} {saving && <Spinner size="sm" animation="grow" as="span" />}
            </Button>
        </Form>
    );
}
